// Lot 534 — Filtres, réglages et recadrage des médias (stories, publications, reels).
// Un seul objet `fx` : { preset, brightness, contrast, saturation, flip } (+ crop/rotate pour les PHOTOS, cuites dans le
// fichier via un canvas). Photos → le fichier envoyé est déjà filtré/recadré (rendu identique partout : fil, vignettes,
// liens). Vidéos → `fx` est stocké avec le média et appliqué en CSS par les lecteurs (filter + miroir).
// Les filtres sont des matrices de couleur (spec CSS Filter Effects) : même maths en CSS (aperçu) et sur le canvas (cuisson),
// sans dépendre de CanvasRenderingContext2D.filter (absent de Safari).
import { smoothCanvasFaces } from "./faceSmooth";

export const FX_PRESETS = [
  { key: "normal", label: "Normal", ops: [] },
  { key: "warm", label: "Chaleur", ops: [["sepia", 0.25], ["saturate", 1.3], ["brightness", 1.05]] },
  { key: "bright", label: "Clair", ops: [["brightness", 1.15], ["contrast", 1.05]] },
  { key: "bw", label: "Noir & blanc", ops: [["grayscale", 1], ["contrast", 1.1]] },
  { key: "vintage", label: "Vintage", ops: [["sepia", 0.45], ["contrast", 0.95], ["brightness", 0.95], ["saturate", 0.8]] },
  { key: "cool", label: "Froid", ops: [["hue-rotate", 15], ["saturate", 1.1], ["brightness", 1.03]] },
  { key: "soft", label: "Doux", ops: [["contrast", 0.9], ["brightness", 1.08], ["saturate", 0.9]] },
  { key: "vivid", label: "Vif", ops: [["saturate", 1.6], ["contrast", 1.1]] },
  { key: "fade", label: "Fané", ops: [["contrast", 0.85], ["brightness", 1.1], ["saturate", 0.75]] },
  { key: "gold", label: "Or", ops: [["sepia", 0.35], ["saturate", 1.5], ["contrast", 1.05], ["brightness", 1.02], ["hue-rotate", -8]] },
];

export const DEFAULT_FX = { preset: "normal", brightness: 100, contrast: 100, saturation: 100, flip: false };
export const ASPECTS = [
  { key: "original", label: "Originale", value: null },
  { key: "free", label: "Forme libre", value: 0 },
  { key: "3:4", label: "3:4", value: 3 / 4 },
  { key: "9:16", label: "9:16", value: 9 / 16 },
  { key: "1:1", label: "1:1", value: 1 },
];

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
export const normalizeFx = (fx) => {
  if (!fx || typeof fx !== "object") return { ...DEFAULT_FX };
  return {
    preset: FX_PRESETS.some((p) => p.key === fx.preset) ? fx.preset : "normal",
    brightness: clamp(Number(fx.brightness) || 100, 50, 150),
    contrast: clamp(Number(fx.contrast) || 100, 50, 150),
    saturation: clamp(Number(fx.saturation) || 100, 0, 200),
    flip: !!fx.flip,
  };
};
export const hasFx = (fx) => {
  if (!fx) return false;
  const n = normalizeFx(fx);
  return n.preset !== "normal" || n.brightness !== 100 || n.contrast !== 100 || n.saturation !== 100 || n.flip;
};
// Objet à stocker (null si tout est neutre) — vidéos (post.video_fx, album[i].fx, story media_transform.fx).
export const fxToStore = (fx) => (hasFx(fx) ? normalizeFx(fx) : null);

// Liste d'opérations (preset + réglages) → chaîne CSS `filter`.
const opsOf = (fx) => {
  const n = normalizeFx(fx);
  const ops = [...(FX_PRESETS.find((p) => p.key === n.preset)?.ops || [])];
  if (n.brightness !== 100) ops.push(["brightness", n.brightness / 100]);
  if (n.contrast !== 100) ops.push(["contrast", n.contrast / 100]);
  if (n.saturation !== 100) ops.push(["saturate", n.saturation / 100]);
  return ops;
};
export const fxFilterCss = (fx) => opsOf(fx).map(([k, v]) => (k === "hue-rotate" ? `hue-rotate(${v}deg)` : `${k}(${v})`)).join(" ") || "none";
// Style complet pour un <img>/<video> (filtre + miroir) ; `extraTransform` = transform déjà présent (zoom story…).
export const fxStyle = (fx, extraTransform = "") => {
  if (!hasFx(fx)) return extraTransform ? { transform: extraTransform } : undefined;
  const n = normalizeFx(fx);
  const transform = [extraTransform, n.flip ? "scaleX(-1)" : ""].filter(Boolean).join(" ");
  return { filter: fxFilterCss(n), ...(transform ? { transform } : {}) };
};

// ---- Matrices de couleur (5x5 homogène, lignes R G B A W) -----------------------------------------------------------
const I5 = () => [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1];
const mul = (a, b) => { // a·b (appliquer b puis a)
  const out = new Array(25).fill(0);
  for (let r = 0; r < 5; r++) for (let c = 0; c < 5; c++) { let s = 0; for (let k = 0; k < 5; k++) s += a[r * 5 + k] * b[k * 5 + c]; out[r * 5 + c] = s; }
  return out;
};
const rgb = (m) => { const o = I5(); for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) o[r * 5 + c] = m[r * 3 + c]; return o; };
const opMatrix = (k, v) => {
  switch (k) {
    case "grayscale": { const a = 1 - v; return rgb([0.2126 + 0.7874 * a, 0.7152 - 0.7152 * a, 0.0722 - 0.0722 * a, 0.2126 - 0.2126 * a, 0.7152 + 0.2848 * a, 0.0722 - 0.0722 * a, 0.2126 - 0.2126 * a, 0.7152 - 0.7152 * a, 0.0722 + 0.9278 * a]); }
    case "sepia": { const a = 1 - v; return rgb([0.393 + 0.607 * a, 0.769 - 0.769 * a, 0.189 - 0.189 * a, 0.349 - 0.349 * a, 0.686 + 0.314 * a, 0.168 - 0.168 * a, 0.272 - 0.272 * a, 0.534 - 0.534 * a, 0.131 + 0.869 * a]); }
    case "saturate": return rgb([0.213 + 0.787 * v, 0.715 - 0.715 * v, 0.072 - 0.072 * v, 0.213 - 0.213 * v, 0.715 + 0.285 * v, 0.072 - 0.072 * v, 0.213 - 0.213 * v, 0.715 - 0.715 * v, 0.072 + 0.928 * v]);
    case "hue-rotate": { const a = (v * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a); return rgb([0.213 + c * 0.787 - s * 0.213, 0.715 - c * 0.715 - s * 0.715, 0.072 - c * 0.072 + s * 0.928, 0.213 - c * 0.213 + s * 0.143, 0.715 + c * 0.285 + s * 0.140, 0.072 - c * 0.072 - s * 0.283, 0.213 - c * 0.213 - s * 0.787, 0.715 - c * 0.715 + s * 0.715, 0.072 + c * 0.928 + s * 0.072]); }
    case "brightness": return rgb([v, 0, 0, 0, v, 0, 0, 0, v]);
    case "contrast": { const o = rgb([v, 0, 0, 0, v, 0, 0, 0, v]); const t = 0.5 - 0.5 * v; o[4] = t; o[9] = t; o[14] = t; return o; }
    default: return I5();
  }
};
export const fxMatrix = (fx) => opsOf(fx).reduce((acc, [k, v]) => mul(opMatrix(k, v), acc), I5());

// Applique le filtre (sans miroir) aux pixels d'un canvas déjà dessiné — partagé par la cuisson et la caméra.
export const applyFxToCanvas = (ctx, w, h, fx) => {
  if (!hasFx({ ...normalizeFx(fx), flip: false })) return;
  const id = ctx.getImageData(0, 0, w, h);
  applyMatrix(id.data, fxMatrix(fx));
  ctx.putImageData(id, 0, 0);
};

const applyMatrix = (data, m) => {
  const identity = m.every((v, i) => v === I5()[i]);
  if (identity) return;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] / 255, g = data[i + 1] / 255, b = data[i + 2] / 255;
    data[i] = clamp((m[0] * r + m[1] * g + m[2] * b + m[4]) * 255, 0, 255);
    data[i + 1] = clamp((m[5] * r + m[6] * g + m[7] * b + m[9]) * 255, 0, 255);
    data[i + 2] = clamp((m[10] * r + m[11] * g + m[12] * b + m[14]) * 255, 0, 255);
  }
};

const loadImage = (src) => new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = src; });

// Cuit filtre + miroir + rotation + recadrage (+ lissage visage « Beauté » 0..3, lot 536b) dans une nouvelle image JPEG.
// `crop` = { x, y, width, height } en pixels de l'image source NON tournée (format react-easy-crop), `rotate` en degrés
// (0/90/180/270). Retourne un File.
export const bakeImageFx = async (file, { fx, crop = null, rotate = 0, beauty = 0 } = {}, { maxSide = 2160, quality = 0.92 } = {}) => {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const sw = img.naturalWidth, sh = img.naturalHeight;
    const c = crop && crop.width > 0 && crop.height > 0 ? crop : { x: 0, y: 0, width: sw, height: sh };
    const rot = ((Math.round(rotate / 90) * 90) % 360 + 360) % 360;
    const swap = rot === 90 || rot === 270;
    const outW = swap ? c.height : c.width, outH = swap ? c.width : c.height;
    const k = Math.min(1, maxSide / Math.max(outW, outH));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(outW * k));
    canvas.height = Math.max(1, Math.round(outH * k));
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rot * Math.PI) / 180);
    if (fx?.flip) ctx.scale(-1, 1);
    ctx.drawImage(img, c.x, c.y, c.width, c.height, (-c.width * k) / 2, (-c.height * k) / 2, c.width * k, c.height * k);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (beauty > 0) await smoothCanvasFaces(ctx, canvas.width, canvas.height, beauty);
    applyFxToCanvas(ctx, canvas.width, canvas.height, fx);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    const base = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}-fx.jpg`, { type: "image/jpeg" });
  } finally { URL.revokeObjectURL(url); }
};

// Recadre une image depuis une URL distante (média déjà en ligne, ex. brouillon) → File.
export const fileFromUrl = async (url, name = "photo.jpg") => {
  const blob = await (await fetch(url)).blob();
  return new File([blob], name, { type: blob.type || "image/jpeg" });
};
